import { or, and, not, createMachine } from 'xstate';

import { routePath } from 'config/route-config';
import { actionUrl } from 'config/static-content';
import { events } from 'machines/config/events';
import { guards } from 'machines/config/guards';
import { stringToBool } from 'utils/misc';

export const directSalesMachineConfig = {
  id: 'directSalesMachineRevamp',
  context: {
    guards: {}
  },
  initial: 'home',
  states: {
    home: {
      on: {
        NEXT: [
          {
            target: 'energyNeeds',
            guard: 'hasAnyMeterValidationRequired'
          },
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'energyNeeds'
          }
        ],

        BLOCK_JOURNEY: {
          target: 'businessUpdatesAnnouncement'
        }
      },
      meta: {
        path: routePath.home
      }
    },

    businessUpdatesAnnouncement: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmail'
        },
        ALLOW_JOURNEY: {
          target: 'home'
        }
      },

      meta: {
        path: routePath.businessUpdatesAnnouncement
      }
    },

    businessUpdatesSubmitEmail: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmailSuccess'
        }
      },
      meta: {
        path: routePath.businessUpdatesSubmitEmail
      }
    },

    businessUpdatesSubmitEmailSuccess: {
      meta: {
        path: routePath.businessUpdatesSubmitEmailSuccess
      }
    },

    meterpointValidation: {
      on: {
        NEXT: [
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'energyNeeds'
          }
        ],
        GUIDE_CLICK: {
          target: 'meterPointGuide'
        }
      },
      meta: {
        path: routePath.meterpointValidation
      }
    },

    meterPointGuide: {
      meta: {
        path: routePath.meterpointGuide
      }
    },

    selectOptions: {
      on: {
        ELIGIBLE_FUEL: {
          target: 'energyNeeds'
        },
        EDIT_ADDRESS: {
          target: 'home'
        },
        HOME_MOVE: [
          {
            target: 'externalContactUs',
            description: `or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])`,

            guard: or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])
          },
          {
            target: 'externalMoveIn',
            guard: 'isBGECustomer'
          },
          {
            target: 'externalBgClassicHomeMove'
          }
        ],
        MIXED_METERS: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.selectOptions
      }
    },

    energyNeeds: {
      on: {
        NEXT: [
          {
            target: 'meterpointValidation',
            guard: 'meterValidationRequired'
          },
          {
            target: 'evTariffInformation',
            guard: 'showEvTariffInformation'
          },
          {
            target: 'paymentMethod',
            guard: 'isPrePayOnly'
          },
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.energyNeeds
      }
    },

    evTariffInformation: {
      on: {
        NEXT: [
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.evTariffInformation
      }
    },

    paymentMethod: {
      on: {
        NEXT: [
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.prepayInfo
      }
    },

    consumptionEntry: {
      on: {
        NEXT: [
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.consumptionEntry
      }
    },

    personalInformationSummary: {
      on: {
        NEXT: {
          target: 'externalBgSalesApp'
        },
        CHANGE_ADDRESS: {
          target: 'home'
        },
        CHANGE_CONSUMPTION: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.personalInformationSummary
      }
    },

    quote: {
      on: {
        NEXT: {
          target: 'tariffDetails'
        },
        EMAIL_QUOTE: {
          target: 'emailQuote'
        },
        CONSUMPTION: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.quote
      }
    },

    tariffDetails: {
      on: {
        NEXT: {
          target: 'createAccount'
        }
      },
      meta: {
        path: routePath.tariffDetails
      }
    },

    emailQuote: {
      on: {
        NEXT: {
          target: 'quote'
        }
      },
      meta: {
        path: routePath.emailQuote
      }
    },

    createAccount: {
      on: {
        NEXT: [
          {
            target: 'directDebitType',
            description:
              "and(['hasMultipleDirectDebitTypes', 'isAnyDirectDebit'])",
            guard: and(['hasMultipleDirectDebitTypes', 'isAnyDirectDebit'])
          },
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'psrEntry'
          }
        ],
        CHANGE_ADDRESS: { target: 'home' }
      },
      meta: {
        path: routePath.createAccount
      }
    },

    directDebitType: {
      on: {
        NEXT: {
          target: 'directDebit'
        }
      },
      meta: {
        path: routePath.directDebitType
      }
    },

    directDebit: {
      on: {
        NEXT: {
          target: 'psrEntry'
        }
      },

      meta: {
        path: routePath.directDebit
      }
    },

    psrEntry: {
      on: {
        NEXT: [
          {
            target: 'psrCapture',
            guard: 'hasPSR'
          },
          {
            target: 'userPreferences'
          }
        ]
      },
      meta: {
        path: routePath.psrEntry
      }
    },

    psrCapture: {
      on: {
        NEXT: {
          target: 'userPreferences'
        }
      },
      meta: {
        path: routePath.psrCapture
      }
    },

    userPreferences: {
      on: {
        NEXT: {
          target: 'orderSummary'
        }
      },
      meta: {
        path: routePath.userPreferences
      }
    },

    orderSummary: {
      on: {
        NEXT: {
          target: 'orderComplete'
        }
      },
      meta: {
        path: routePath.orderSummary
      }
    },

    orderComplete: {
      meta: {
        path: routePath.orderComplete
      }
    },

    externalBgSalesApp: {
      meta: {
        path: actionUrl.bgSalesApp
      }
    },

    externalContactUs: {
      meta: { path: actionUrl.contact }
    },
    externalBgClassicHomeMove: {
      meta: { path: actionUrl.bgClassicHomeMoveUrl }
    },

    externalMoveIn: {
      meta: { path: actionUrl.moveIn }
    }
  },
  ...events
};

const { VITE_XSTATE_DEBUG } = process.env;
const debug = stringToBool(VITE_XSTATE_DEBUG);
// for VS extesnion
/* istanbul ignore next -- @preserve */
debug &&
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QQJYCcwGMAuBlAhgDZwCy+mAFigHZgBKYAbvgLYAOAdBQPYtgDEAOQCiADQAqAbQAMAXUSg23WCmwpu1BSAAeiAMwAmPRz0AWAIzmArAHYr5gJy2AHADZTAGhABPRM4c2HFbOejZmrlYGkQ56AL6xXqgYOATEsGSUNPRMrJw8fEJiUubySCBKKmoaWroIhsZmlrb2TjZunj5+poF2zgbmrkbSDgY28YnoWHhEpORUtAzM7Fy8AiISkgalisqq6ppltfUmFtZ2ji7uXr4IpgEcBg4WNqPSegGuNq7jIElTqbNMgscst8msipI9NtyrsqgdQEcjCcmudWu1rognoFTMMonppDYLHprD8-ikZuk5llFrkVgUAEIAGQA8gBhADSAH0AFLMgCqdBEAE0ZNCKntqod9EjGmcWpcOjdzHpghxuvj8QEbOYDKZSZNyWkMvNsktOAAjACuKlosFgfLYEHw2DgAEFqNRuJbqJgwHxqNhChs5FpxXCapj-BxnNrpNIrCrpK5nFYHBiEMrItGHOZTPirJFTAXvglfgbpkaqcCzRwrTa4PbHc63R6vT6-WAA-xXYyWQB1Hn8wXCEUhsph-YR25hEzOaTWSI2JdWZOKxDK6TODjuc5RAuRRz65IVwEmmnLOtZO0Op0u2C4S3mliqYQsfAoQhBqRjnaVSdSjNk3MDhzBsJx8ReAxNz0dNMwMbNc3zQtiyPf4KWNakQU4PgXTQJQaGwAA1IgUFvfYv1FUNYX-BFpQaU5mguNork6W5ujVZMIlMAw+lCbpUMNU9MJrHCwDw7gCOIwhSOdcj1mKMVqMlWi6hlBjUQVdN3kCdx8VcaRTHcKIvgEk9KSBU1aVE8TJJIsiNAorYqL-ZSdDo5E5SY9FWIMUYgheGw3gJN4Yh1UyAXMs8sI4az8IDKSZLhfgAHE+QASQAEWETlWUZNKOUo8clPhNzVPolF5WYtc6nY5xQNcUC8xzQK9BLCZjwijDq1pWAwGIHBmTYOFYH4YQ8uStKmWygAxPkxsK38JRK2oHCjGNzDjBMrCTFM01Y1MHBMXUGpXJC+nC9Cq0s5Zev67BBuG0aMrS8ROVdDKMroYRcFwBaYRc5bEDuUwODsPR-FMPNIiGVx0zCaQTC+ZNnBjQKwLiUsyTMrrrs4W6pge-YRoACWZEhspIZlCOEP6J1cla1tjeNE2TVM4aTbcvgGcGLCMVwMfatDKws888b6gmhqJ-hSfJzlKepyQSmcpap1Ww71s2lndvTCJXGjbbbDjIxLBGC7haimt8YGyWNBJsmKapmmnKKgHVcZjbme21m9puBN1e1exrGJPNuLNoTuuWZ9tEgEgwFwkb5Np4q3f9j2tp2tnWLuBGDNTUDtT0Yk0bDyLhNpTsxKgbxBDASAE4hH9-pVgCNuTDgAhzJNIcMZVnFggkQIOvNdrz1aS5x0WOArtAq5ruuKKVl3m5UgY6pAsCEwJXzoPTXUrBAwzfIcfnXH0-wrHHq7J+n2fa4geuNmdxbwxboD1-AreoJCHXhnbgl-AcEmIwphnB6kxuWTqV9oo32rnfB+UgoTKxfivN+oEP6QR3qxNehhHjZ2CAZQKBhL4i2gbQGesD56J1MIpV2r815oM3hg7+rFQjAQCO8Da211TuGIRbcujBxD4DQCgAAZiItK1ARHcDQG+JKidG500BggYB+9YxuCMF-cGu95ygxVIFPoyMQEDF4WXZYTBBHCLERIqRMjZIOUTovZ+NFSoqNBhtdRhhoJ9x8vYIIDVrA6hiDEewF9wEdUuiQmsbB8DeH9NgWO2AeAQAogo5OAFXFqMGJ4kI3ibgNT1jgvoBJHCfH4mEoW4dcYcGibEzs8S45JIXjQ5eLizCqPcVkzRuSgYjFnKfVwAQFx8TAYLQSpcI6cEwLbS07A4TCADGgbwKTmnINKoXFcQQVxBOOqA6qkN97bSggEaCbRj4jLLOE82pjJnTNmfseZ2BFlNKQc42ojxwZqmJA4M5m1NxaRiOvCIzFBjmBjCqExEzqliVgBoIg1jpGyP2A+Fgb4nnyJWa8xAjwOYxCLAZZMYQDAsRuHcfendhgjGKcSCFVS2DQthYQeFti4TItRUs1kxNXSCGStld6n1vq-VSbQlSuoOZgTnOESCYRqqt0CH0KC+IoZPCsOcrGkDIm0jpWgGF1A4WSIRXY6grKhHss5dy7KrJmSCFwHyEgAAFcQaUrVJ2FS4wBetcVcIJYYYlQM8wcCNsMfRoR-A8PKWMie0UACOlpuAumWS8+m+hCTGDsHYSGhIVzzisOmHEesVUQwGHGGIRKaWTxjXGgQwgSCujSoyTkABFPkzJxA0yFS0o4KbQbNAzUWfS1gtIJm3BYecZglwxm4qE0Z2MoE1grfGy11rbUOqdYIF1Hb9D7O3DmVeoR4yjBzSwpwnMgVuBBTGNqFyKnjKqdgIRoiREZTju+Qg8D12rNqDiXxww7gF3hk0XNHMC1PCLcMH1ZboFvg-A22N8b0WJqUeDTc25dqEiTI4bpdQ3jRmsKS+MgwXgCyvRG2dtJMAYGbK6TAUzvSBng0vD9fq-LaWsDxNGwx0wxARoFaQURAltD0RBms5GwCUeo22OjEJHFN0Y2xZjgzIgxmCj7dcBI9b+IMjEVqOZwZCbIxRl0VGaNdkTk-GTmK5OBBY4p9jKmMygsOsuFVdgkzakJHp5YImxPGcDByrlPK3ofS+j9d9Fmv3AR-YM0I+IAOsUsCjB4Y6ExmG6FBMY4aZ0auWFjJ95pVDiG8HShNDGwsBC3DxYk+6Q2cY2Rh7FOp-FRA85wHLYA8uSeDBipNCADx6x4hmuqhdC42H7gMkwBGUaPDzvzZr1TYBoAeWihuXWlEqpVJ8yw0X9LsMPUqHaQRcEquPlEPozhZtsHm4tpZDiVtTjW-vPMm2wjbbCLt9coKtxPF8rmUChdQHnfm6yfAQ1LQYGK047rLgtzxhAfpJc3F-Cja3NEIsdxBjBB4rN60Yk7UYBEWJTsvo33ttk-pdw+sBgNSMPDOz8XgIDPeN0TcUE7jmFm9IiAYljVLc6whqc-NHjtyMO8FGERC6wVBfToJTOims-iKWT0nP4BlDVREvh7A+cAQALSw1Ylrsl3zDdG8N61WbYJNcryHZ8Y+gyEddwMLBRoDwiy5lOd0AYRCMvqvVxaa0V5Gy3hbJ6b0vo4kW9Kp8LcoEQg6h1ISUBME4sqmMPGGIKMQEjEiLNy8toA-NnvI+Z82BXwvvD7UZUgCQJnNAaxo75hHdDtT+DUB-goie+nd765tY-e55vPnh8T4XxQcIA+ajDYy9YtHSBUBdgBl2EAU4BvKfUzN4z232bsUJLxTsoaiftwHdZ0Lu3aVm2BkhEnRvuOOOt-YGSpaUiYA9+R-XjH-oowQF5i0uxVaR3vlgRdyuLNlbPdDbNQMrhDkomDELqtPimxqtJhvDEEJDO4EBODHYFOsRplj7jFCgNHBAAktCnvi8CDASEWGjIXLmHcLmpXv0IzsfNtO8O4KqhAmrl3jAnPPfHvohHrBXqvH0CqkHOmKmMjtii8POIMDiO3pgZ3pCuYvelYvqsys4oolOP0AZEEBqEmP2hQbrjcHvCYLmL5GgWtpuOdjEnEgkkknvv0KBAYZ8MELPm-jrBtAGvPpqN8txG0LNlMmATMqAVdnvsSKmCYKtDxoQm4K1LmqEEEOniqqmHnHYOdvSrqoyooYihoNzjcBAaoSCqDB3N8vOP-swiSimA8PGLGE4N8vzMwZcpUuWrBo-iVt1q1IYMOjiPPk1M5v8vvLsgmBEGrCEOlh3qwZCnepYo+s+h+OAeZs0TumqGBEYHVL5JEGYIBnrNqHPsnnOPFkMdISMVUn6C+jBpWoEXMd0CMODDqG3qsSwiAg8KMHugUUbGzl7vsZPF5oZuJrRnvpOnrMMNDA1GYK3hLnOEdE0Jnk4GjrNq1u1gVnSj8XOD0EMJYBmv4jVgUqfESiCu8P0JeqrlcpCjCaoHvttGvASKmMsQZKCqNodIwrpIMXuADgtgslkTMatl8q4S8OBH-tSXFqCvBAMpEOjltI8C8cMQSbSoDsDtgKDo0dkQBCuFmCFBtBmh7Jhq3FuHPo9vGCFFIfiXUdFNjmgLjmAPjhgO2NMSoQqf4AjDPpHgZK5iCcBG4I4DqR9kWHqSwRKZPBzlzjMmyk-gMDpJDN0CmG8MmLTpLtGA1IAttO6Vnq8d6dFL6WgKyLwGwMQC6E-nmPBBsbicqKBJYE6dGa6XGbEZ6bUTetfNoLhCkfSFABFK6GwGwIEQuAGu8MfCqJwtxIniSsfK4XOLYIhCMC8BgfqVWdAjWWJCkWmQGOQNgHyJaWkivO8AjCUh-rXqznDNxAGjmIuC4KMCmLNmAFOWgHWVAKyIQPgHaCgJgMTKsCQNwIwHKWyVOBXmudXhYJEHXkITiO2U8AMoZLxG8MeaeSkY+c+RIlwauVXu4DXt+VuaxECgGqCv2sfPOKtCWPEEAA */ ...directSalesMachineConfig
    },
    {
      guards
    }
  );
